import * as Dialog from '@radix-ui/react-dialog';
import { transparentize } from 'polished';
import styled from 'styled-components';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { COLORS, LAYOUT, ORDER } from '@/constants/styles';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import { safePolished } from '@/utils/safePolished';

/* ─── Modal shell ─────────────────────────────────────────────────── */

export const StyledCustomizeModal = styled.div`
  bottom: 0;
  height: 100%;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  transform-style: preserve-3d;
  z-index: ${ORDER.CUSTOMIZE};
`;

export const Overlay = styled(Dialog.Overlay)`
  background: rgba(26, 26, 26, 0.16);
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  transition: all 0.4s ease;
  z-index: 0;
`;

export const CustomizeModalContent = styled(Dialog.Content)`
  height: 100%;
  padding: 0 0 env(safe-area-inset-bottom);
  width: 100%;
  display: flex;
`;

/* ─── Container and scroll area ───────────────────────────────────── */

export const StyledCustomizeContainer = styled.div(({ theme }) => {
  return {
    background: theme.modals.background_color ?? 'white',
    borderRadius: `${theme.modals.border_radius ?? 0}px`,
    height: 'auto',
    maxHeight: `calc(100vh - ${LAYOUT.GUTTER * 4}px)`,
    minHeight: '400px',
    margin: 'auto',
    maxWidth: `${LAYOUT.CUSTOMIZE_MODAL_MAX_WIDTH}px`,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
    ...getFontStyles(theme.modals.font, ['color']),
    '@media (max-width: 767px)': {
      borderRadius: 0,
      margin: 0,
      maxHeight: '100vh',
    },
  };
});

const FIXED_MOBILE_FOOTER_HEIGHT = 110;
export const StyledCustomizeScrollContent = styled.div`
  height: auto;
  max-height: calc(100vh - ${LAYOUT.GUTTER * 4 + 150}px);
  margin-bottom: ${LAYOUT.CUSTOMIZE_FOOTER_MAX_HEIGHT}px;
  min-height: 400px;
  overflow-y: scroll;
  padding-bottom: 40px;

  // https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-overflow-scrolling
  -webkit-overflow-scrolling: touch;

  @media (max-width: 767px) {
    height: 100vh;
    max-height: calc(100vh - ${FIXED_MOBILE_FOOTER_HEIGHT}px);
    padding-bottom: ${FIXED_MOBILE_FOOTER_HEIGHT}px;
  }
`;

export const StyledCustomizeFooter = styled.div<{
  $validationErrorIsVisible: boolean;
}>`
  ${({ theme }) => `
    background: ${theme.modals.footer_color ?? theme.modals.background_color ?? 'inherit'};
    border-top: 1px solid ${theme.modals.border_color ?? 'inherit'};
    box-shadow: 0 0 20px 0 ${theme.modals.border_color ?? 'inherit'};
  `}

  bottom: 0px;
  // Ensure box shadow only applies to the top of the footer
  clip-path: inset(-20px 0px 0px 0px);
  padding: ${LAYOUT.GUTTER}px;
  position: absolute;
  width: 100%;

  // Remove top padding if toast error is visible
  ${({ $validationErrorIsVisible }) =>
    $validationErrorIsVisible &&
    `
    padding-top: 0px;
  `}

  @media (max-width: 767px) {
    padding: ${LAYOUT.GUTTER / 2}px;
  }
`;

export const StyledToastError = styled.div<{ $isVisible: boolean }>`
  color: ${COLORS.ERROR_RED};
  background-color: ${COLORS.ERROR_RED_BACKGROUND};
  height: 50px;
  text-align: center;
  font-size: 14px;
  font-family: ${({ theme }) => theme.primary_font.medium.font_family};
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  margin: ${`0 -${LAYOUT.GUTTER}px ${LAYOUT.GUTTER}px -${LAYOUT.GUTTER}px`};
`;

export const StyledPdpCloseButton = styled.button`
  align-items: center;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  border: none;
  cursor: pointer;
  display: flex;
  height: 40px;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: ${LAYOUT.GUTTER}px;
  top: ${LAYOUT.GUTTER / 2}px;
  vertical-align: middle;
  width: 40px;
  z-index: 1;
`;

/* ─── Footer (quantity + add-to-cart) ─────────────────────────────── */

export const StyledAddDetails = styled.div`
  align-items: center;
  flex-direction: row-reverse;
  display: flex;
  justify-content: right;
  margin: 0 0 ${LAYOUT.GUTTER / 2}px;

  > p {
    flex: 1;
    font-family: ${({ theme }) => theme.primary_font.medium.font_family};
    font-size: 14px;
    font-weight: normal;
    margin: 0;
    text-align: right;
  }

  > span {
    font-size: 14px;
    margin: 0 ${LAYOUT.GUTTER / 2}px;
  }
`;

export const StyledAddToBag = styled(StyledPrimaryButton)<{
  disabled: boolean;
}>`
  border-radius: 30px;
  width: 100%;

  ${({ disabled }) =>
    disabled &&
    `
    cursor: not-allowed;
    font-style: italic;
  `}

  @media (min-width: 768px) {
    height: 56px;
    line-height: 56px;
  }
`;

/* ─── Product detail (header) ─────────────────────────────────────── */

interface StyledCustomizeProductDetailProps {
  $customizable: boolean;
}

export const StyledCustomizeProductDetail = styled.div<StyledCustomizeProductDetailProps>`
  border-bottom: solid 1px
    ${({ $customizable, theme }) => ($customizable ? theme.modals.border_color : 'transparent')};
  display: flex;

  @media (max-width: 577px) {
    flex-direction: column;
  }
`;

interface ProductImageProps {
  src: string;
}

export const StyledProductImage = styled.div<ProductImageProps>`
  background-image: ${({ src }) => src && `url(${src})`};
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  border-radius: 8px;
  flex-shrink: 0;
  height: 180px;
  margin: ${LAYOUT.GUTTER}px;
  width: 200px;

  @media (max-width: 577px) {
    height: 200px;
    margin: ${LAYOUT.GUTTER}px ${LAYOUT.GUTTER}px 0;
    width: calc(100% - ${LAYOUT.GUTTER * 2}px);
  }
`;

export const StyledCustomizeProductInfo = styled.div(({ theme }) => {
  return {
    padding: `${LAYOUT.GUTTER}px ${LAYOUT.GUTTER}px ${LAYOUT.GUTTER}px 0`,
    width: '100%',

    '& > div': {
      marginBottom: '8px',
    },

    '& div': {
      alignItems: 'baseline',
      display: 'flex',

      '& > h2': {
        flex: 1,
      },
    },

    h2: {
      fontSize: '22px',
      margin: '0 40px 0 0',
      textAlign: 'left',
      ...getFontStyles(theme.modals.secondary_font, [
        'color',
        'font_family',
        'font_weight',
        'font_style',
        'line_height',
      ]),
    },

    p: {
      margin: '6px 0 0',
      fontSize: '14px',
      ...getFontStyles(theme.modals.font),

      a: {
        textDecoration: 'underline',
        transition: 'opacity 0.2s linear',

        '&:hover': {
          opacity: 0.7,
        },
      },
    },

    '@media (max-width: 577px)': {
      padding: `${LAYOUT.GUTTER / 2}px ${LAYOUT.GUTTER}px`,
    },
  };
});

export const StyledWarningLabels = styled.span`
  img {
    height: 20px;
    padding-left: 5px;
  }
`;

/* ─── Option groups ───────────────────────────────────────────────── */

export const StyledTopOptionContainer = styled.div`
  margin: 0 ${LAYOUT.GUTTER}px 0;
  position: relative;
`;

export const StyledNestedOptionContainer = styled.div`
  width: 100%;

  &:first-child {
    > div {
      border: none;
      margin-top: 0px;
    }
  }
`;

export const StyledOptionGroupHeader = styled.div(({ theme }) => {
  return {
    borderTop: `1px solid ${theme.modals.border_color}`,
    marginTop: '20px',
    padding: '20px 0 8px 0',
    marginBottom: '4px',
    'h3,p': {
      ...getFontStyles(theme.modals.font, ['color', 'font_family', 'font_weight', 'line_height']),
    },
    h3: {
      fontSize: '18px',
      fontWeight: 700,
      margin: 0,
      textAlign: 'left',

      span: {
        color: '#666666',
        fontSize: '14px',
        fontWeight: 400,
        paddingLeft: '10px',
      },
    },

    p: {
      fontSize: '14px',
      color: '#666666',
      margin: '4px 0 0',
    },
  };
});

export const StyledOptionGroupHelperText = styled.div`
  color: ${COLORS.ERROR_RED};
  margin-top: ${LAYOUT.GUTTER / 4}px;
  font-size: 14px;
`;

export const StyledOptionQuantityHelperText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

/* ─── Option grid ─────────────────────────────────────────────────── */

export const StyledOptionGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 4px 0;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

/* ─── Option card (container) ─────────────────────────────────────── */

interface StyledOptionContainerProps {
  $is_inverted: boolean;
  selected: boolean;
  $disabled: boolean;
}

export const StyledOptionContainer = styled.div<StyledOptionContainerProps>`
  ${({ $is_inverted, selected, $disabled, theme }) => {
    const isActive = $is_inverted ? !selected : selected;

    if ($disabled) {
      return `
        background: #F7F7F7;
        border: 1.5px solid #EDEDED;
      `;
    }

    if (isActive) {
      return `
        background: ${safePolished(transparentize)(
          '0.92',
          theme.modals.active_border_color ?? '#E8862A',
        )};
        border: 2px solid ${theme.modals.active_border_color ?? '#E8862A'};
      `;
    }

    return `
      background: ${theme.modals.background_color ?? 'white'};
      border: 1.5px solid #EDEDED;
    `;
  }}

  border-radius: 8px;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  position: relative;
  text-align: left;
`;

/* ─── Option card (button) ────────────────────────────────────────── */

export const StyledOptionCard = styled.button`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  flex: auto;
  text-align: left;
  width: 100%;
  padding: 0;

  &:disabled {
    cursor: default;
  }
`;

/* ─── Modifier image ──────────────────────────────────────────────── */

interface StyledModifierImageProps {
  url?: string;
  supportsQuantitySelection: boolean;
}

export const StyledModifierImage = styled.div<StyledModifierImageProps>`
  ${({ url }) => url && `background-image: url(${url})`};
  background-repeat: no-repeat;
  ${({ url }) => !url && `background-color: ${COLORS.BACKGROUNDGRAY}`};
  background-size: cover;
  background-position: center center;
  border-radius: 8px 8px 0 0;
  width: 100%;
  height: 120px;
`;

/* ─── Option content ──────────────────────────────────────────────── */

interface StyledOptionContentProps {
  selected?: boolean;
  $is_inverted?: boolean;
  $disabled?: boolean;
}

export const StyledOptionContent = styled.div<StyledOptionContentProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  position: relative;
  width: 100%;

  ${({ $disabled }) => $disabled && 'opacity: 0.5;'}

  h4 {
    color: ${({ selected, $is_inverted, theme }) =>
      ($is_inverted ? !selected : selected)
        ? theme.modals.active_font_color
        : theme.modals.font.color};
    font-family: ${({ theme }) => theme.primary_font.medium.font_family};
    margin: 0;
  }

  p {
    color: ${({ theme }) => theme.modals.font.color};
    margin: 4px 0 0;
  }
`;

export const StyledOptionTextArea = styled.div`
  flex: 1;
  min-width: 0;
`;

export const StyledOptionNamePriceRow = styled.div`
  @media (max-width: 1024px) {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }
`;

export const StyledOptionPrice = styled.p`
  && {
    margin: 4px 0 0;
    font-size: 14px;
    white-space: nowrap;

    @media (max-width: 1024px) {
      margin: 0;
    }
  }
`;

export const StyledOptionName = styled.div`
  display: flex;
  p {
    font-family: ${({ theme }) => theme.primary_font.bold.font_family};
  }
`;

export const StyledOptionLabel = styled.div`
  img {
    height: 15px;
    margin: 2px 0 0;
    padding-left: 6px;
  }
`;

/* ─── Selection indicator (checkbox / radio) ──────────────────────── */

export const StyledSelectionIndicator = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.modals.active_border_color ?? '#E8862A'};
`;

/* ─── Per-option quantity stepper ─────────────────────────────────── */

interface StyledQuantitySelectorProps {
  selected: boolean;
}

export const StyledQuantitySelector = styled.div<StyledQuantitySelectorProps>`
  background: none;
  display: flex;
  min-height: ${LAYOUT.QUANTITY_SELECTOR_HEIGHT}px;
  max-width: 100%;
  justify-content: center;
  border-top: 1px solid
    ${({ selected, theme }) => (selected ? theme.modals.active_border_color : COLORS.LIGHTGRAY)};
  align-items: center;
`;

interface StyledChangeQuantityButtonProps {
  increment: boolean;
}

export const StyledChangeQuantityButton = styled.button<StyledChangeQuantityButtonProps>`
  border: none;
  background: none;
  color: ${({ disabled, theme }) =>
    disabled ? COLORS.LIGHTGRAY : theme.modals.active_border_color};
  cursor: pointer;
  margin: 0 ${LAYOUT.GUTTER}px;
  font-family: ${({ theme }) => theme.primary_font.bold.font_family};
  font-size: 24px;
  flex: 1;
  text-align: ${({ increment }) => (increment ? 'left' : 'right')};
`;

/* ─── Warning labels ──────────────────────────────────────────────── */

export const StyledWarningLabel = styled.div`
  background: ${({ theme }) => safePolished(transparentize)('.8', theme.modals.font.color)};
  padding: ${LAYOUT.GUTTER}px;

  div {
    display: flex;
    margin: 0 0 ${LAYOUT.GUTTER / 2}px;

    &:last-of-type {
      margin: 0;
    }
  }

  p {
    color: ${({ theme }) => theme.modals.font.color};
    margin: 0;
  }

  img {
    margin: 3px ${LAYOUT.GUTTER / 4}px 0 0;
    max-height: 15px;
  }
`;
